import { useFeatureAccess } from '@/hooks/use-feature-access.hook';

/**
 * Whether the current org may use the AI Copilot.
 *
 * AppSumo accounts can't use the Copilot — UNLESS they've also subscribed to a
 * paid Cleanlist plan (stackable-credits model), which supersedes the AppSumo
 * carve-out. `isAppSumoOnly` is the backend-authoritative signal from the
 * entitlements matrix (/billing/feature-access): true when the org has an
 * AppSumo grant AND no stacked paid plan. The agent endpoint also 403s as a
 * backstop.
 *
 * Extracted so the four Copilot entry points (chat search box, AI sidebar,
 * search landing, top bar) share one definition.
 *
 * Fails closed: `isAppSumoOnly` reads false until the entitlements arrive, so
 * access waits for them (and for an active organization). A failed first
 * read leaves the entry points hidden; a failed background refetch keeps the
 * last verdict.
 *
 * `denied` is the settled "no": AppSumo-only, no active organization, or a
 * failed first read. Unlike `!canUseCopilot` it is false while the
 * entitlements load, so a surface that was opened on purpose (the dock after
 * onboarding or a Stripe return) waits for the answer instead of being shut.
 */
export function useCopilotAccess(): {
  canUseCopilot: boolean;
  isAppSumoOnly: boolean;
  denied: boolean;
} {
  const { isAppSumoOnly, isReady, organizationId, error } = useFeatureAccess();
  const settled = isReady && Boolean(organizationId);
  const denied = settled
    ? isAppSumoOnly
    : Boolean(error) || (isReady && !organizationId);
  return { canUseCopilot: settled && !isAppSumoOnly, isAppSumoOnly, denied };
}
